import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import Card from "../Cards/Card";
import { Box, Button, Grid, Modal, Tooltip } from "@mui/material";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import ComboHeader from "../ComboHeader/ComboHeader";
import Kpi from "../KPI/Kpi";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useHistory } from "react-router-dom";

function OverviewCard({
  app,
  accumulated,
  cardTitle,
  firstKpiTitle,
  secondKpiTitle,
  firstKpiInfo,
  secondKpiInfo,
  singleGroupView,
}) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });
  const [openModal, setOpenModal] = useState(false);

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 85:
        return "#E95248";
      case percentage < 100:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  const initializeKpis = (group) => {
    Object.keys(group).forEach((key) => {
      group[key]["value"] = Kpi({
        app,
        objectId: group[key]["valueId"],
        updatesWithSelection: true,
      });
      group[key]["goalValue"] = Kpi({
        app,
        objectId: group[key]["goalValueId"],
        updatesWithSelection: true,
      });
      group[key]["percentage"] = Kpi({
        app,
        objectId: group[key]["percentageId"],
        updatesWithSelection: true,
      });
      group[key]["score"] = Kpi({
        app,
        objectId: group[key]["scoreId"],
        updatesWithSelection: true,
      });
      // set percentage Num
      const num = group[key]["percentage"]
        ? parseFloat(group[key]["percentage"])
        : 0;
      group[key]["percentageNum"] = num > 100 ? 100 : num;
      // set percentage color
      group[key]["percentageColor"] = getColorFromPercentage(
        group[key]["percentageNum"]
      );
    });
  };

  initializeKpis(firstKpiInfo);
  initializeKpis(secondKpiInfo);

  return (
    <Card app={app} style={{ width: "100%", height: "100%" }}>
      <Grid
        className="overview-card"
        container
        flexDirection={"column"}
        justifyContent={"space-between"}
        alignItems={"flex-start"}
        sx={{ height: "100%" }}
      >
        <Grid
          container
          item
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"flex-start"}
          sx={{ height: "100%" }}
          xs
        >
          <Grid
            item
            sx={{
              height: `${notFullHd ? "32" : "48"}px !important`,
            }}
          >
            <ComboHeader
              reference={{
                title: {
                  hard: cardTitle,
                },
              }}
            />
          </Grid>
          <Grid
            container
            item
            flexDirection={"row"}
            justifyContent={"flex-start"}
            alignItems={"flex-start"}
            gap={2}
            sx={{ height: "100%" }}
            xs
          >
            {/* FIRST KPI */}
            <Grid
              className="kpi-border"
              container
              item
              flexDirection={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              xs
              rowGap={2}
            >
              {/* FIRST KPI HEADER */}
              <Grid
                container
                flexDirection={"row"}
                justifyContent={
                  singleGroupView ? "space-between" : "flex-start"
                }
                alignItems={"flex-start"}
                className="title"
              >
                <Grid
                  item
                  sx={{
                    width: "100%",
                  }}
                >
                  <Box className="kpi-title" component={"span"}>
                    {firstKpiTitle}
                  </Box>
                </Grid>
              </Grid>

              {/* FIRST KPI BODY */}
              {Object.values(firstKpiInfo).map((kpi, index) => (
                <Grid
                  key={index}
                  container
                  item
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"flex-start"}
                  rowGap={1.5}
                >
                  <Grid
                    container
                    flexDirection={"column"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    rowGap={1}
                    sx={{
                      marginTop: "16px",
                    }}
                  >
                    {/* SCORE */}
                    {singleGroupView && (
                      <Grid
                        container
                        item
                        flexDirection={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"flex-start"}
                      >
                        <Grid item xs>
                          <Box className="score" component={"span"}>
                            {`${kpi.score} pontos`}
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    {/* SUBTITLE */}
                    <Grid
                      container
                      item
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                    >
                      <Box className="subtitle" component={"span"}>
                        {kpi.title}
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    rowGap={1}
                  >
                    {/* VALUE */}
                    <Grid
                      className={`value-wrapper`}
                      container
                      item
                      xs={12}
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Box
                        component={"span"}
                        className="value"
                        style={{
                          color: kpi.percentageColor,
                        }}
                      >
                        {kpi.value}
                      </Box>
                    </Grid>
                    {/* PROGRESS BAR */}
                    <Grid
                      container
                      item
                      flexDirection={"column"}
                      justifyContent={"flex-start"}
                      alignItems={"flex-start"}
                      style={{
                        width: "100%",
                      }}
                      xs={12}
                    >
                      <ProgressBar
                        key={"Sell out"}
                        bgColor={kpi.percentageColor}
                        completed={kpi.percentageNum}
                      />
                    </Grid>

                    {/* GOAL AND PERCENTAGE */}
                    <Grid
                      className={`goal-percentage-wrapper`}
                      container
                      item
                      flexDirection={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      xs
                    >
                      {/* GOAL VALUE */}
                      <Box component={"div"} className={`goal-wrapper`}>
                        <Tooltip title={kpi.goalValue}>
                          <Box component={"span"} className="goal-value">
                            {kpi.goalValue}
                          </Box>
                        </Tooltip>
                        <Box component={"span"} className="goal-text">
                          Meta
                        </Box>
                      </Box>
                      {/* PERCENTAGE VALUE */}
                      <Box component={"div"} className={`percentage-wrapper`}>
                        <Tooltip title={kpi.percentage}>
                          <Box
                            component={"span"}
                            className="percentage-value"
                            style={{
                              color: kpi.percentageColor,
                            }}
                          >
                            {kpi.percentage}
                          </Box>
                        </Tooltip>
                        <Box component={"span"} className="percentage-text">
                          Ating.
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  {index !== 2 && (
                    <Box component={"div"} className="bottom-divider" />
                  )}
                </Grid>
              ))}
            </Grid>

            {/* SECOND KPI */}
            <Grid
              className="kpi-border"
              container
              item
              flexDirection={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              xs
              rowGap={2}
            >
              {/* SECOND KPI HEADER */}
              <Grid
                container
                flexDirection={"row"}
                justifyContent={
                  singleGroupView ? "space-between" : "flex-start"
                }
                alignItems={"flex-start"}
                className="title"
              >
                <Grid
                  item
                  sx={{
                    width: "100%",
                  }}
                >
                  <Box className="kpi-title" component={"span"}>
                    {secondKpiTitle}
                  </Box>
                </Grid>
              </Grid>

              {/* SECOND KPI BODY */}
              {!accumulated
                ? Object.values(secondKpiInfo)
                    .slice(0, 3)
                    .map((kpi, index) => (
                      <Grid
                        key={index}
                        container
                        item
                        flexDirection={"column"}
                        justifyContent={"center"}
                        alignItems={"flex-start"}
                        rowGap={1.5}
                      >
                        <Grid
                          container
                          flexDirection={"column"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          rowGap={1}
                          sx={{
                            marginTop: "16px",
                          }}
                        >
                          {/* SCORE */}
                          {singleGroupView && (
                            <Grid
                              container
                              item
                              flexDirection={"row"}
                              justifyContent={"flex-start"}
                              alignItems={"flex-start"}
                            >
                              <Grid item xs>
                                <Box className="score" component={"span"}>
                                  {`${kpi.score} pontos`}
                                </Box>
                              </Grid>
                            </Grid>
                          )}
                          {/* SUBTITLE */}
                          <Grid
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                          >
                            <Box className="subtitle" component={"span"}>
                              {kpi.title}
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          rowGap={1}
                        >
                          {/* VALUE */}
                          <Grid
                            className={`value-wrapper`}
                            container
                            item
                            xs={12}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                            sx={{
                              width: "100%",
                            }}
                          >
                            <Box
                              component={"span"}
                              className="value"
                              style={{
                                color: kpi.percentageColor,
                              }}
                            >
                              {kpi.value}
                            </Box>
                          </Grid>
                          {/* PROGRESS BAR */}
                          <Grid
                            container
                            item
                            flexDirection={"column"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                            style={{
                              width: "100%",
                            }}
                            xs={12}
                          >
                            <ProgressBar
                              key={"Sell out"}
                              bgColor={kpi.percentageColor}
                              completed={kpi.percentageNum}
                            />
                          </Grid>

                          {/* GOAL AND PERCENTAGE */}
                          <Grid
                            className={`goal-percentage-wrapper`}
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            xs
                          >
                            {/* GOAL VALUE */}
                            <Box component={"div"} className={`goal-wrapper`}>
                              <Tooltip title={kpi.goalValue}>
                                <Box component={"span"} className="goal-value">
                                  {kpi.goalValue}
                                </Box>
                              </Tooltip>
                              <Box component={"span"} className="goal-text">
                                Meta
                              </Box>
                            </Box>
                            {/* PERCENTAGE VALUE */}
                            <Box
                              component={"div"}
                              className={`percentage-wrapper`}
                            >
                              <Tooltip title={kpi.percentage}>
                                <Box
                                  component={"span"}
                                  className="percentage-value"
                                  style={{
                                    color: kpi.percentageColor,
                                  }}
                                >
                                  {kpi.percentage}
                                </Box>
                              </Tooltip>
                              <Box
                                component={"span"}
                                className="percentage-text"
                              >
                                Ating.
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                        {index !== 2 && (
                          <Box
                            component={"div"}
                            className="bottom-divider"
                          ></Box>
                        )}
                      </Grid>
                    ))
                : Object.values(secondKpiInfo)
                    .slice(-1)
                    .map((kpi, index) => (
                      <Grid
                        key={index}
                        container
                        item
                        flexDirection={"column"}
                        justifyContent={"flex-start"}
                        alignItems={"flex-start"}
                        rowGap={1.5}
                      >
                        <Grid
                          container
                          flexDirection={"column"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          rowGap={1}
                          sx={{
                            marginTop: "16px",
                          }}
                        >
                          {/* SCORE */}
                          {singleGroupView && (
                            <Grid
                              container
                              item
                              flexDirection={"row"}
                              justifyContent={"flex-start"}
                              alignItems={"flex-start"}
                            >
                              <Grid item xs>
                                <Box className="score" component={"span"}>
                                  {`${kpi.score} pontos`}
                                </Box>
                              </Grid>
                            </Grid>
                          )}

                          {/* SUBTITLE */}
                          <Grid
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                          >
                            <Box className="subtitle" component={"span"}>
                              {kpi.title}
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          flexDirection={"row"}
                          justifyContent={"flex-start"}
                          alignItems={"flex-start"}
                          rowGap={1}
                        >
                          {/* VALUE */}
                          <Grid
                            className={`value-wrapper`}
                            container
                            item
                            xs={12}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                            sx={{
                              width: "100%",
                            }}
                          >
                            <Box
                              component={"span"}
                              className="value"
                              style={{
                                color: kpi.percentageColor,
                              }}
                            >
                              {kpi.value}
                            </Box>
                          </Grid>
                          {/* PROGRESS BAR */}
                          <Grid
                            container
                            item
                            flexDirection={"column"}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                            style={{
                              width: "100%",
                            }}
                            xs={12}
                          >
                            <ProgressBar
                              key={"Sell out"}
                              bgColor={kpi.percentageColor}
                              completed={kpi.percentageNum}
                            />
                          </Grid>

                          {/* GOAL AND PERCENTAGE */}
                          <Grid
                            className={`goal-percentage-wrapper`}
                            container
                            item
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            xs
                          >
                            {/* GOAL VALUE */}
                            <Box component={"div"} className={`goal-wrapper`}>
                              <Tooltip title={kpi.goalValue}>
                                <Box component={"span"} className="goal-value">
                                  {kpi.goalValue}
                                </Box>
                              </Tooltip>
                              <Box component={"span"} className="goal-text">
                                Meta
                              </Box>
                            </Box>
                            {/* PERCENTAGE VALUE */}
                            <Box
                              component={"div"}
                              className={`percentage-wrapper`}
                            >
                              <Tooltip title={kpi.percentage}>
                                <Box
                                  component={"span"}
                                  className="percentage-value"
                                  style={{
                                    color: kpi.percentageColor,
                                  }}
                                >
                                  {kpi.percentage}
                                </Box>
                              </Tooltip>
                              <Box
                                component={"span"}
                                className="percentage-text"
                              >
                                Ating.
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          direction="column"
                          justifyContent="flex-end"
                          alignItems="flex-start"
                          xs
                        >
                          <Button
                            className="detail-button"
                            variant="outlined"
                            onClick={() => setOpenModal(true)}
                            endIcon={<CropFreeOutlinedIcon />}
                          >
                            &nbsp;DETALHES
                          </Button>
                        </Grid>
                        <Modal
                          open={openModal}
                          onClose={() => setOpenModal(false)}
                        >
                          <Grid
                            className="card"
                            container
                            flexDirection={"column"}
                            justifyContent={"center"}
                            sx={{
                              top: notFullHd ? "52%" : "44%",
                              left: notFullHd ? "86%" : "88%",
                              transform: "translate(-50%, -50%)",
                              position: "absolute",
                              height: "auto",
                              width: notFullHd ? "20%" : "20%",
                            }}
                          >
                            {/* MODAL CONTENT */}
                            <Grid
                              className="modal-content"
                              item
                              container
                              flexDirection={"column"}
                              justifyContent={"flex-start"}
                              alignItems={"flex-start"}
                              sx={{
                                height: "100%",
                              }}
                              xs
                            >
                              {Object.values(secondKpiInfo)
                                .slice(0, 3)
                                .map((kpi, index) => (
                                  <Grid
                                    key={index}
                                    container
                                    item
                                    flexDirection={"column"}
                                    justifyContent={"center"}
                                    alignItems={"flex-start"}
                                    rowGap={1.5}
                                  >
                                    <Grid
                                      container
                                      flexDirection={"column"}
                                      justifyContent={"flex-start"}
                                      alignItems={"flex-start"}
                                      rowGap={1}
                                      sx={{
                                        marginTop: "16px",
                                      }}
                                    >
                                      {/* SUBTITLE */}
                                      <Grid
                                        container
                                        item
                                        flexDirection={"row"}
                                        justifyContent={"flex-start"}
                                        alignItems={"flex-start"}
                                      >
                                        <Box
                                          className="subtitle"
                                          component={"span"}
                                        >
                                          {kpi.title}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                    <Grid
                                      container
                                      item
                                      flexDirection={"row"}
                                      justifyContent={"flex-start"}
                                      alignItems={"flex-start"}
                                      rowGap={1}
                                    >
                                      {/* VALUE */}
                                      <Grid
                                        className={`value-wrapper`}
                                        container
                                        item
                                        xs={12}
                                        flexDirection={"row"}
                                        justifyContent={"flex-start"}
                                        alignItems={"flex-start"}
                                        sx={{
                                          width: "100%",
                                        }}
                                      >
                                        <Box
                                          component={"span"}
                                          className="value"
                                          style={{
                                            color: kpi.percentageColor,
                                          }}
                                        >
                                          {kpi.value}
                                        </Box>
                                      </Grid>
                                      {/* PROGRESS BAR */}
                                      <Grid
                                        container
                                        item
                                        flexDirection={"column"}
                                        justifyContent={"flex-start"}
                                        alignItems={"flex-start"}
                                        style={{
                                          width: "100%",
                                        }}
                                        xs={12}
                                      >
                                        <ProgressBar
                                          key={"Sell out"}
                                          bgColor={kpi.percentageColor}
                                          completed={kpi.percentageNum}
                                        />
                                      </Grid>

                                      {/* GOAL AND PERCENTAGE */}
                                      <Grid
                                        className={`goal-percentage-wrapper`}
                                        container
                                        item
                                        flexDirection={"row"}
                                        justifyContent={"space-between"}
                                        alignItems={"center"}
                                        xs
                                      >
                                        {/* GOAL VALUE */}
                                        <Box
                                          component={"div"}
                                          className={`goal-wrapper`}
                                        >
                                          <Tooltip title={kpi.goalValue}>
                                            <Box
                                              component={"span"}
                                              className="goal-value"
                                            >
                                              {kpi.goalValue}
                                            </Box>
                                          </Tooltip>
                                          <Box
                                            component={"span"}
                                            className="goal-text"
                                          >
                                            Meta
                                          </Box>
                                        </Box>
                                        {/* PERCENTAGE VALUE */}
                                        <Box
                                          component={"div"}
                                          className={`percentage-wrapper`}
                                        >
                                          <Tooltip title={kpi.percentage}>
                                            <Box
                                              component={"span"}
                                              className="percentage-value"
                                              style={{
                                                color: kpi.percentageColor,
                                              }}
                                            >
                                              {kpi.percentage}
                                            </Box>
                                          </Tooltip>
                                          <Box
                                            component={"span"}
                                            className="percentage-text"
                                          >
                                            Ating.
                                          </Box>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                    {index !== 2 && (
                                      <Box
                                        component={"div"}
                                        className="bottom-divider"
                                      ></Box>
                                    )}
                                  </Grid>
                                ))}
                            </Grid>
                          </Grid>
                        </Modal>
                      </Grid>
                    ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}

OverviewCard.defaultProps = {
  app: null,
};

OverviewCard.propTypes = {
  app: PropTypes.object,
};

export default OverviewCard;
